import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import clsx from "clsx"
import { type ReactNode, useState } from "react"

import { theme } from "../../theme"
import { Text } from "../Text"
import styles from "./Tooltip.module.css"

export const TooltipProvider = TooltipPrimitive.Provider

interface BaseTooltipProps {
  children: ReactNode
  side?: Parameters<typeof TooltipPrimitive.Content>[0]["side"]
  sideOffset?: Parameters<typeof TooltipPrimitive.Content>[0]["sideOffset"]
  align?: Parameters<typeof TooltipPrimitive.Content>[0]["align"]
  delayDuration?: Parameters<typeof TooltipPrimitive.Root>[0]["delayDuration"]
  open?: boolean
  /**
   * By passing an element here, the tooltip will be rendered as the last child of this element.
   * If not, the tooltip will be rendered as the last child of the document.body element.
   *
   * Passing a specific element is useful i.e. when you're trying to display
   * a Tooltip in a container that has specified the `z-index` value.
   * Examples of such containers are Tiptap's BubbleMenu and SliceMachineModal.
   */
  container?: Element | null
  visible?: boolean
  /**
   * By enabling the stable mount a `div` will be rendered around your trigger item.
   *
   * This is a workaround for a bug where mounting the Tooltip portal
   * inside another Portal rendered item causes the Tooltip to not open.
   *
   * @param stableMount
   * @defaultValue `false` This will wrap the trigger element in a Fragment.
   */
  stableMount?: boolean
  /**
   * Animation to use when showing/hiding the tooltip.
   *
   * @defaultValue "fade"
   */
  animation?: "fade" | "none"
  disableHoverableContent?: boolean
}

interface TextTooltipProps extends BaseTooltipProps {
  variant?: "text"
  content: string
  title?: string
}

interface CustomTooltipProps extends BaseTooltipProps {
  variant: "custom"
  content: ReactNode
  title?: never
}

export type TooltipProps = TextTooltipProps | CustomTooltipProps

export function Tooltip(props: TooltipProps) {
  const {
    children,
    content,
    title,
    side,
    sideOffset,
    align,
    open: controlledOpen,
    container,
    visible = true,
    stableMount = false,
    delayDuration = 300,
    variant = "text",
    animation = "fade",
    disableHoverableContent,
  } = props

  const initialOpenState = visible ? (controlledOpen ?? false) : false
  const [open, setOpen] = useState(initialOpenState)

  function handleOnOpenChange(open: boolean) {
    if (!visible) return

    setOpen(open)
  }

  if (controlledOpen !== undefined && open !== controlledOpen)
    handleOnOpenChange(controlledOpen)

  const animationClass =
    animation !== "none" &&
    styles[`animation-${animation}-${open ? "entering" : "exiting"}`]

  // This is a workaround for a bug where mounting the Tooltip portal
  // inside another Portal rendered item causes the Tooltip to not open.
  const _children = stableMount ? <div>{children}</div> : children

  return (
    <TooltipPrimitive.Root
      delayDuration={delayDuration}
      onOpenChange={handleOnOpenChange}
      open={open}
      disableHoverableContent={disableHoverableContent}
    >
      <TooltipPrimitive.Trigger asChild>{_children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal container={container}>
        <TooltipPrimitive.Content
          avoidCollisions
          className={clsx(
            styles.content,
            animationClass,
            styles[`content-${variant}`],
          )}
          collisionPadding={parseFloat(theme.space[16])}
          side={side}
          sideOffset={sideOffset}
          align={align}
          /* Scroll event propagation is stopped to fix scroll inside dialogs
          https://github.com/prismicio/editor/pull/1164
          https://github.com/radix-ui/primitives/issues/1159 */
          onWheel={(event) => event.stopPropagation()}
        >
          {variant === "text" ? (
            <>
              {title && (
                <Text color="white" variant="smallBold">
                  {title}
                </Text>
              )}

              <Text color="white" variant="small">
                {content}
              </Text>

              <TooltipPrimitive.Arrow className={styles.arrow} />
            </>
          ) : (
            content
          )}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}
